import { PaymentMethod } from '../models/Organization';

/** True when the org/tutor/room offers Stripe pass checkout. */
export function isOnlinePassPurchaseAvailable(
  methods: PaymentMethod[] | null | undefined,
): boolean {
  return (methods ?? []).includes(PaymentMethod.ONLINE);
}

/** True when the org/tutor/room offers cash pass checkout. */
export function isCashPassPurchaseAvailable(
  methods: PaymentMethod[] | null | undefined,
): boolean {
  return (methods ?? []).includes(PaymentMethod.CASH);
}

/** Show Buy when either cash or online pass checkout is supported. */
export function isPassPurchaseAvailable(
  methods: PaymentMethod[] | null | undefined,
): boolean {
  return isOnlinePassPurchaseAvailable(methods) || isCashPassPurchaseAvailable(methods);
}

export function isOnlinePassPurchaseAvailableForRooms(
  rooms: Array<{ supportedPaymentMethods?: PaymentMethod[] | null }> | null | undefined,
): boolean {
  return (rooms ?? []).some((room) => isOnlinePassPurchaseAvailable(room.supportedPaymentMethods));
}

export function isPassPurchaseAvailableForRooms(
  rooms: Array<{ supportedPaymentMethods?: PaymentMethod[] | null }> | null | undefined,
): boolean {
  return (rooms ?? []).some((room) => isPassPurchaseAvailable(room.supportedPaymentMethods));
}

/** Union of payment methods across rooms (for space pass checkout UI). */
export function resolvePassCheckoutPaymentMethodsForRooms(
  rooms: Array<{ supportedPaymentMethods?: PaymentMethod[] | null }> | null | undefined,
): PaymentMethod[] {
  const set = new Set<PaymentMethod>();
  for (const room of rooms ?? []) {
    for (const method of room.supportedPaymentMethods ?? []) {
      set.add(method);
    }
  }
  return Array.from(set);
}

/**
 * Methods offered in the pass purchase dialog.
 * ONLINE is first (and the default) when both methods exist.
 */
export function resolvePassCheckoutPaymentMethods(
  methods: PaymentMethod[] | null | undefined,
): PaymentMethod[] {
  const available = methods ?? [];
  const ordered: PaymentMethod[] = [];
  if (available.includes(PaymentMethod.ONLINE)) ordered.push(PaymentMethod.ONLINE);
  if (available.includes(PaymentMethod.CASH)) ordered.push(PaymentMethod.CASH);
  return ordered;
}

export function defaultPassCheckoutPaymentMethod(
  methods: PaymentMethod[] | null | undefined,
): PaymentMethod | undefined {
  return resolvePassCheckoutPaymentMethods(methods)[0];
}
